import { z } from "zod";
import { TovMapConfigSchema } from "../../config/schemas";
import {
  TovMapAttemptStatusSchema,
  TovMapperGroupKeySchema,
  TovMapStartOutcomeSchema,
} from "../../enums/tov-mapping";
import { TovGenericEvidenceRefSchema } from "./mapping-evidence";

/**
 * Durable recovery artifacts for TOV mapping. A mapping attempt is created by
 * the backend before it publishes the map message; the worker checkpoints every
 * page/group result against that attempt so a failure or process death never
 * loses successful extraction work. Bump this when the checkpoint payload shape
 * changes so stored artifacts from older mappers are rejected, not misread.
 */
export const TOV_MAP_ARTIFACT_FORMAT_VERSION = 1;

/**
 * Wire cap on one checkpoint POST. The worker chunks its batches to this, so
 * the PostHog-tunable extraction concurrency is never coupled to it.
 */
export const TOV_MAP_CHECKPOINT_BATCH_LIMIT = 64;

/**
 * Machine-readable ConflictError detail the backend attaches when a draft or
 * checkpoint write is refused because the attempt is no longer running. The
 * worker matches on this to abort quietly instead of dead-lettering.
 */
export const TOV_MAP_ATTEMPT_NOT_RUNNING = "attempt_not_running";

/** Canonical wire form of the worker's per page/group coverage diagnostics. */
export const TovMapCoverageRecordSchema = z.object({
  sourceId: z.uuid(),
  pageNumber: z.number().int().min(0),
  locator: z.string(),
  groupKey: TovMapperGroupKeySchema,
  toolCalled: z.boolean(),
  attempted: z.number().int().min(0),
  accepted: z.number().int().min(0),
  rejected: z.number().int().min(0),
  noCandidateReason: z.string().nullable(),
  validatorErrors: z.array(z.string()),
  failed: z.boolean(),
});
export type TovMapCoverageRecord = z.infer<typeof TovMapCoverageRecordSchema>;

/** Canonical wire form of a validated candidate, evidence already resolved. */
export const TovMapAcceptedCandidateSchema = z.object({
  groupKey: TovMapperGroupKeySchema,
  table: z.string().min(1),
  validatedRow: z.record(z.string(), z.unknown()),
  rawRow: z.record(z.string(), z.unknown()),
  parentKey: z.string().optional(),
  evidence: z.array(TovGenericEvidenceRefSchema),
});
export type TovMapAcceptedCandidate = z.infer<
  typeof TovMapAcceptedCandidateSchema
>;

export const TovMapCheckpointPayloadSchema = z.object({
  coverage: TovMapCoverageRecordSchema,
  accepted: z.array(TovMapAcceptedCandidateSchema),
});
export type TovMapCheckpointPayload = z.infer<
  typeof TovMapCheckpointPayloadSchema
>;

/** One scan unit: (source, page, group) is the checkpoint identity. */
export const TovMapCheckpointKeySchema = z.object({
  sourceId: z.uuid(),
  pageNumber: z.number().int().min(0),
  groupKey: TovMapperGroupKeySchema,
});
export type TovMapCheckpointKey = z.infer<typeof TovMapCheckpointKeySchema>;

/**
 * First worker contact for an attempt. The worker sends the compatibility
 * snapshot (pinned config, prompt and artifact format versions, the sorted
 * source set it will scan); the backend records it on first start and rejects
 * a resume whose snapshot no longer matches — mixing extraction output from
 * different prompts/configs in one draft would be unauditable.
 */
export const TovMapAttemptStartRequestSchema = z.object({
  config: TovMapConfigSchema,
  promptVersion: z.string().min(1),
  artifactFormatVersion: z.number().int().positive(),
  sourceIds: z.array(z.uuid()).min(1),
});
export type TovMapAttemptStartRequest = z.infer<
  typeof TovMapAttemptStartRequestSchema
>;

export const TovMapAttemptStartResponseSchema = z.object({
  // superseded: a newer attempt owns the version — abort quietly, no failure.
  // incompatible: snapshot mismatch — the attempt is closed; fail loudly.
  outcome: TovMapStartOutcomeSchema,
  reason: z.string().nullable(),
});
export type TovMapAttemptStartResponse = z.infer<
  typeof TovMapAttemptStartResponseSchema
>;

export const TovMapCheckpointBatchRequestSchema = z.object({
  checkpoints: z
    .array(
      TovMapCheckpointKeySchema.extend({
        payload: TovMapCheckpointPayloadSchema,
      }),
    )
    .min(1)
    .max(TOV_MAP_CHECKPOINT_BATCH_LIMIT),
});
export type TovMapCheckpointBatchRequest = z.infer<
  typeof TovMapCheckpointBatchRequestSchema
>;

export const TovMapCheckpointBatchResponseSchema = z.object({
  ok: z.literal(true),
  stored: z.number().int().min(0),
  // Not "running" means a newer attempt superseded this one mid-run; the worker
  // aborts quietly instead of burning more LLM spend.
  attemptStatus: TovMapAttemptStatusSchema,
});
export type TovMapCheckpointBatchResponse = z.infer<
  typeof TovMapCheckpointBatchResponseSchema
>;

export const TOV_MAP_CHECKPOINT_PAGE_LIMIT = 100;

export const TovMapCheckpointListResponseSchema = z.object({
  checkpoints: z.array(
    TovMapCheckpointKeySchema.extend({
      payload: TovMapCheckpointPayloadSchema,
    }),
  ),
  nextOffset: z.number().int().min(0).nullable(),
});
export type TovMapCheckpointListResponse = z.infer<
  typeof TovMapCheckpointListResponseSchema
>;

export const TovMapAttemptSummaryResponseSchema = z.object({
  attempt: z
    .object({
      id: z.uuid(),
      versionId: z.uuid(),
      status: TovMapAttemptStatusSchema,
      promptVersion: z.string().nullable(),
      artifactFormatVersion: z.number().int().nullable(),
      config: TovMapConfigSchema.nullable(),
      sourceIds: z.array(z.uuid()).nullable(),
      failureReason: z.string().nullable(),
      createdAt: z.string(),
      updatedAt: z.string(),
      finishedAt: z.string().nullable(),
    })
    .nullable(),
  groups: z.array(
    z.object({
      groupKey: TovMapperGroupKeySchema,
      checkpointed: z.number().int().min(0),
      failed: z.number().int().min(0),
      acceptedCandidates: z.number().int().min(0),
    }),
  ),
});
export type TovMapAttemptSummaryResponse = z.infer<
  typeof TovMapAttemptSummaryResponseSchema
>;
